import { theme } from 'antd';
import React, { type PropsWithChildren } from 'react';
import styled from 'styled-components';

import store from 'stores/RootStore';
import { AnimationPanelViews } from 'types/AnimationPanelViews';

import closeIconUrl from './closeIcon.svg';

// See the note in components/UI/Table/Column.tsx: read at module scope to
// track antd's palette across upgrades rather than hardcoding hexes.
//
// Caveat for a future dark mode: this resolves once, at import time, so it
// cannot react to a runtime theme switch. Naming the intent is the win
// here - actually switching themes would want either theme.useToken() (a
// hook, so the tokens would have to be threaded into these module-scope
// styled components as props) or CSS variable mode, <ConfigProvider
// theme={{ cssVar: { key: 'app' } }}>, which lets these same rules say
// var(--ant-color-bg-container) with no plumbing and does follow a switch.
// Note cssVar requires an explicit `key` on React 17.
const { colorInfoText, colorBorder } = theme.getDesignToken();

interface HighlightableProps {
  $showUnreadIndicator?: boolean;
}

const Container = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  height: 56px;
  justify-content: space-between;
  padding-right: 12px;
  border-bottom: 1px solid ${colorBorder};
  border-top: 1px solid ${colorBorder};
`;

const Icon = styled.img`
  height: 24px;
  margin: 9px;
  width: 24px;
  z-index: 0;
`;

const LeftContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 7px;
  justify-content: flex-start;
`;

// ::before rule that achieves the unread-bullet also appears in
// client/src/components/UI/ChatBox/Comments.tsx
const Label = styled.span<HighlightableProps>`
  position: relative;
  font-size: 16px;

  &::before {
    display: ${(p) => (p.$showUnreadIndicator ? 'block' : 'none')};
    position: absolute;
    left: -0.5em;
    top: -0.5em;
    content: '●';
    color: ${colorInfoText};
  }
`;

export type DrawerHeaderProps = PropsWithChildren<{
  handleOnClose?: () => void;
  showUnreadIndicator?: boolean;
  label?: string;
}>;

function DrawerHeader({
  children,
  handleOnClose,
  showUnreadIndicator,
  label,
}: DrawerHeaderProps) {
  const store_animation = store.animation;

  return (
    <Container>
      <LeftContainer>
        <Icon
          src={closeIconUrl}
          onClick={() => {
            store_animation?.setPanel(AnimationPanelViews.NONE);
            handleOnClose?.();
          }}
        />
        <Label $showUnreadIndicator={showUnreadIndicator}>{label}</Label>
      </LeftContainer>
      {children}
    </Container>
  );
}

export default DrawerHeader;
